import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { Toaster } from 'sonner';
import { useAdminAuth } from './contexts/AdminAuthContext';
import AdminLoginPage from './pages/AdminLoginPage';
import DashboardPage from './pages/DashboardPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import ActivitiesPage from './pages/ActivitiesPage';
import NewActivityPage from './pages/NewActivityPage';
import EditActivityPage from './pages/EditActivityPage';
import ActivityDetailsPage from './pages/ActivityDetailsPage';
import CalendarPage from './pages/CalendarPage';
import ActivityGenerationPage from './pages/ActivityGenerationPage';
import StrategiesPage from './pages/StrategiesPage';
import NewStrategyPage from './pages/NewStrategyPage';
import EditStrategyPage from './pages/EditStrategyPage';
import StrategyDetailsPage from './pages/StrategyDetailsPage';
import ExpertProfilesPage from './pages/ExpertProfilesPage';
import NewExpertProfilePage from './pages/NewExpertProfilePage';
import EditExpertProfilePage from './pages/EditExpertProfilePage';
import ExpertProfileDetailsPage from './pages/ExpertProfileDetailsPage';
import ExpertTipsAdminPage from './pages/ExpertTipsAdminPage';
import NewExpertTipPage from './pages/NewExpertTipPage';
import EditExpertTipPage from './pages/EditExpertTipPage';
import ExpertTipDetailsPage from './pages/ExpertTipDetailsPage';
import ExpertTipTopicsPage from './pages/ExpertTipTopicsPage';
import ExpertTipTopicSchedulePage from './pages/ExpertTipTopicSchedulePage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailsPage from './pages/AdminUserDetailsPage';
import AdminRecommendationsPage from './pages/AdminRecommendationsPage';
import EarlyAccessSignupsPage from './pages/EarlyAccessSignupsPage';
import CommunicationStagesPage from './pages/CommunicationStagesPage';
import DomainsPage from './pages/DomainsPage';
import DomainSkillsPage from './pages/DomainSkillsPage';
import ChildPreferencesPage from './pages/ChildPreferencesPage';
import SparkPlaysPage from './pages/SparkPlaysPage';
import SparkActivityManagePage from './pages/SparkActivityManagePage';
import ActivityOverviewPage from './pages/ActivityOverviewPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AdminLayout } from './components/layout';
import type { AdminRole } from './types/auth';

function DefaultRedirect() {
  const { user } = useAdminAuth();

  const DEFAULT_PAGES: Record<AdminRole, string> = {
    super_admin: '/admin/dashboard',
    creator: '/admin/activities',
  };

  const target = user ? DEFAULT_PAGES[user.role] || '/admin/activities' : '/admin/dashboard';
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <AdminAuthProvider>
      <Routes>
        {/* Public Route: Login */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Super admin only: Dashboard */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <DashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Super admin only: Analytics */}
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <AnalyticsDashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Super admin only: Recommendations */}
        <Route
          path="/admin/recommendations"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <AdminRecommendationsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Super admin only: Early Access Signups */}
        <Route
          path="/admin/early-access"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <EarlyAccessSignupsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Super admin only: Users */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <AdminUsersPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <AdminUserDetailsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Super admin only: Expert Profiles */}
        <Route
          path="/admin/expert-profiles"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <ExpertProfilesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-profiles/new"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <NewExpertProfilePage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-profiles/:id/edit"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <EditExpertProfilePage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-profiles/:id"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <ExpertProfileDetailsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* All authenticated admins: Activities */}
        <Route
          path="/admin/activities"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ActivitiesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activities/new"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <NewActivityPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activities/:id/edit"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EditActivityPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activities/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ActivityDetailsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/calendar"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CalendarPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activities/generate"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ActivityGenerationPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* All authenticated admins: Strategies */}
        <Route
          path="/admin/strategies"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <StrategiesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/strategies/new"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <NewStrategyPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/strategies/:id/edit"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EditStrategyPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/strategies/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <StrategyDetailsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* All authenticated admins: Expert Tips */}
        <Route
          path="/admin/expert-tips"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ExpertTipsAdminPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-tips/new"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <NewExpertTipPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-tips/:id/edit"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EditExpertTipPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-tips/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ExpertTipDetailsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-tip-topics"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ExpertTipTopicsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/expert-tip-topic-schedule"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ExpertTipTopicSchedulePage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Super admin only: Settings */}
        <Route
          path="/admin/settings/communication-stages"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <CommunicationStagesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/domains"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <DomainsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/domain-skills"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <DomainSkillsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/child-preferences"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <ChildPreferencesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/spark-activities/overview"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout fullWidth>
                <ActivityOverviewPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/spark-activities"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout fullWidth>
                <SparkActivityManagePage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/settings/spark-plays"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminLayout>
                <SparkPlaysPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Default redirects — role-aware */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <DefaultRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DefaultRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <DefaultRedirect />
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Toast notifications */}
      <Toaster
        position="top-center"
        richColors
        closeButton
        toastOptions={{
          style: {
            fontFamily: 'inherit',
          },
        }}
      />
    </AdminAuthProvider>
  );
}

export default App;
